// Reconciled against docs/StationBridge-Telephony-Contract.md (v1) section 2.
// params keys are EXACT snake_case. `?` marks optional. The 8 commands flagged (pending)
// in the doc have reserved names + params but their edge handlers return PARAM_INVALID
// until wired - guard those UI affordances.

import { TelephonyCommandNames, type TelephonyCommandName } from './commandNames'
import type { CallForwardKind, PhoneKey } from './enums'

export interface MakeCallParams { extension: string; destination: string; auto_answer?: boolean }
export interface RedialParams { extension: string; destination: string; auto_answer?: boolean }
export interface SendDtmfParams { channel: string; digits: string }
export interface ChannelParams { channel: string }
export interface RedirectParams { channel: string; destination: string; context: string }
export interface TransferParams { channel: string; destination: string; context: string }
export interface ParkParams { channel: string; timeout_channel?: string }
export interface QueueMemberParams { queue: string; member_interface: string }
export interface QueuePauseParams { queue: string; member_interface: string; reason?: string }
export interface SpyParams { target: string; supervisor_channel: string }
export interface RecordStartParams { channel: string; file_name?: string }
export interface SetDndParams { extension: string; enabled: boolean }
export interface SetCallForwardParams { extension: string; kind: CallForwardKind; enabled: boolean; destination?: string }
export interface PhoneKeyPressParams { extension: string; key: PhoneKey; argument?: string }
// Pending (handlers land next pass).
export interface AnswerParams { channel?: string; extension?: string }
export interface ExtensionParams { extension: string }
export interface UnparkParams { parking_space: string; to_extension: string }
export interface VmListParams { mailbox: string }
export interface VmMessageParams { mailbox: string; message_id: string }
export interface VmForwardParams { mailbox: string; message_id: string; to_mailbox: string }

export interface TelephonyCommandParams {
  [TelephonyCommandNames.MakeCall]: MakeCallParams
  [TelephonyCommandNames.Redial]: RedialParams
  [TelephonyCommandNames.SendDtmf]: SendDtmfParams
  [TelephonyCommandNames.Hangup]: ChannelParams
  [TelephonyCommandNames.Redirect]: RedirectParams
  [TelephonyCommandNames.BlindTransfer]: TransferParams
  [TelephonyCommandNames.AttendedTransfer]: TransferParams
  [TelephonyCommandNames.Park]: ParkParams
  [TelephonyCommandNames.Hold]: ChannelParams
  [TelephonyCommandNames.Unhold]: ChannelParams
  [TelephonyCommandNames.Mute]: ChannelParams
  [TelephonyCommandNames.Unmute]: ChannelParams
  [TelephonyCommandNames.QueueLogin]: QueueMemberParams
  [TelephonyCommandNames.QueueLogout]: QueueMemberParams
  [TelephonyCommandNames.QueuePause]: QueuePauseParams
  [TelephonyCommandNames.QueueUnpause]: QueueMemberParams
  [TelephonyCommandNames.Monitor]: SpyParams
  [TelephonyCommandNames.Whisper]: SpyParams
  [TelephonyCommandNames.Barge]: SpyParams
  [TelephonyCommandNames.RecordStart]: RecordStartParams
  [TelephonyCommandNames.RecordStop]: ChannelParams
  [TelephonyCommandNames.SetDnd]: SetDndParams
  [TelephonyCommandNames.SetCallForward]: SetCallForwardParams
  [TelephonyCommandNames.PhoneKeyPress]: PhoneKeyPressParams
  // Pending handlers (return PARAM_INVALID until wired):
  [TelephonyCommandNames.Answer]: AnswerParams
  [TelephonyCommandNames.Reject]: ChannelParams
  [TelephonyCommandNames.Unpark]: UnparkParams
  [TelephonyCommandNames.Pickup]: ExtensionParams
  [TelephonyCommandNames.GetCallForward]: ExtensionParams
  [TelephonyCommandNames.VoicemailList]: VmListParams
  [TelephonyCommandNames.VoicemailDelete]: VmMessageParams
  [TelephonyCommandNames.VoicemailMarkRead]: VmMessageParams
  [TelephonyCommandNames.VoicemailForward]: VmForwardParams
}

export type TypedCommandName = keyof TelephonyCommandParams

// Commands whose edge handler is live this pass (the 20 checkmarks in the contract).
export const ReadyCommands: ReadonlySet<TelephonyCommandName> = new Set<TelephonyCommandName>([
  TelephonyCommandNames.MakeCall, TelephonyCommandNames.Redial, TelephonyCommandNames.SendDtmf,
  TelephonyCommandNames.Hangup, TelephonyCommandNames.Redirect, TelephonyCommandNames.BlindTransfer,
  TelephonyCommandNames.AttendedTransfer, TelephonyCommandNames.Park, TelephonyCommandNames.Hold,
  TelephonyCommandNames.Unhold, TelephonyCommandNames.Mute, TelephonyCommandNames.Unmute,
  TelephonyCommandNames.QueueLogin, TelephonyCommandNames.QueueLogout, TelephonyCommandNames.QueuePause,
  TelephonyCommandNames.QueueUnpause, TelephonyCommandNames.Monitor, TelephonyCommandNames.Whisper,
  TelephonyCommandNames.Barge, TelephonyCommandNames.RecordStart, TelephonyCommandNames.RecordStop,
  TelephonyCommandNames.SetDnd, TelephonyCommandNames.SetCallForward, TelephonyCommandNames.PhoneKeyPress
])
